import { useEffect, useState } from "react"
import "./App.css"
import { EmailScreen } from "./EmailScreen"
import { FailedScreen } from "./FailedScreen"
import { TokenCheckScreen } from "./TokenCheckScreen"
import { PasswordCreateScreen } from "./PasswordCreateScreen"
import { PasswordLoginScreen } from "./PasswordLoginScreen"
import { PromoCodeScreen } from "./PromoCodeScreen"
import { SuccessScreen } from "./SuccessScreen"
import logo from "./logo.png"
import { suggestLocale } from "./api"
import { tx } from "@transifex/native"
import { useT } from "@transifex/react"

export enum Screen {
  PromoCode = "PromoCode",
  Email = "Email",
  PasswordLogin = "PasswordLogin",
  PasswordCreate = "PasswordCreate",
  SuccessExisting = "SuccessExisting",
  SuccessCreated = "SuccessCreated",
  TokenCheckScreen = "TokenCheckScreen",
  Failed = "Failed",
}

tx.init({
  token: "1/2935b76456a796052d4285774e34a6f7df2a6853",
})

const getGympassToken = () => {
  const urlSearchParams = new URLSearchParams(window.location.search)
  const gympassToken = urlSearchParams.get("gympass_activation_token")
  if (gympassToken !== null) {
    return gympassToken
  }
}

const getFitnessOnDemandToken = () => {
  const urlSearchParams = new URLSearchParams(window.location.search)
  const fitnessOnDemandToken = urlSearchParams.get("fitness_on_demand_token")
  if (fitnessOnDemandToken !== null) {
    return fitnessOnDemandToken
  }
}

const getPromoLinkToken = () => {
  const urlSearchParams = new URLSearchParams(window.location.search)
  const promoLinkToken = urlSearchParams.get("promo_link_token")
  if (promoLinkToken !== null) {
    return promoLinkToken
  }
}

const hasPromoLinkError = () => {
  const urlSearchParams = new URLSearchParams(window.location.search)
  return urlSearchParams.has("promo_link_error")
}

const gympassToken = getGympassToken()
const fitnessOnDemandToken = getFitnessOnDemandToken()
const promoLinkToken = getPromoLinkToken()
const promoLinkError = hasPromoLinkError()

let startScreen: Screen
if (promoLinkError) {
  startScreen = Screen.Failed
} else if (gympassToken || fitnessOnDemandToken || promoLinkToken) {
  startScreen = Screen.TokenCheckScreen
} else {
  startScreen = Screen.PromoCode
}

export const App = () => {
  const t = useT()
  const [assignedLocale, setAssignedLocale] = useState<string>()
  const [screen, setScreen] = useState(startScreen)
  const [promoCode, setPromoCode] = useState("")
  const [email, setEmail] = useState("")
  const [rewardDuration, setRewardDuration] = useState<string>()
  const [errorMessage, setErrorMessage] = useState<string>()

  const onTryAgain = () => {
    setScreen(startScreen)
    setPromoCode("")
    setEmail("")
    setRewardDuration(undefined)
    setErrorMessage(undefined)
  }

  const setLocale = async () => {
    const suggestedLocale = await suggestLocale()
    setAssignedLocale(suggestedLocale)
    tx.setCurrentLocale(suggestedLocale)
  }

  useEffect(() => {
    setLocale()
  }, [])

  useEffect(() => {
    if (promoLinkError) {
      document.title = t("Failed - Motosumo")
    } else if (gympassToken) {
      document.title = t("Activate Wellhub - Motosumo")
    } else if (fitnessOnDemandToken) {
      document.title = t("Link Fitness On Demand - Motosumo")
    } else if (promoLinkToken) {
      document.title = t("Get Premium - Motosumo")
    } else {
      document.title = t("Redeem Your Promo Code - Motosumo")
    }
  }, [t])

  return (
    <div className="App">
      <header className="App-header">
        <img className="App-logo" src={logo} alt="" />
        {screen === Screen.TokenCheckScreen && (
          <TokenCheckScreen
            gympassToken={gympassToken}
            fitnessOnDemandToken={fitnessOnDemandToken}
            promoLinkToken={promoLinkToken}
            onComplete={(nextScreen) => {
              setScreen(nextScreen)
            }}
            onFailure={(nextScreen, errorMessage) => {
              setScreen(nextScreen)
              setErrorMessage(errorMessage)
            }}
          />
        )}
        {screen === Screen.PromoCode && (
          <PromoCodeScreen
            onComplete={(nextScreen, promoCode) => {
              setScreen(nextScreen)
              setPromoCode(promoCode)
            }}
            onFailure={(nextScreen, errorMessage) => {
              setScreen(nextScreen)
              setErrorMessage(errorMessage)
            }}
          />
        )}
        {screen === Screen.Email && (
          <EmailScreen
            onComplete={(nextScreen, email) => {
              setScreen(nextScreen)
              setEmail(email)
            }}
          />
        )}
        {screen === Screen.PasswordLogin && (
          <PasswordLoginScreen
            email={email}
            promoCode={promoCode}
            gympassToken={gympassToken}
            fitnessOnDemandToken={fitnessOnDemandToken}
            promoLinkToken={promoLinkToken}
            onComplete={(nextScreen, rewardDuration) => {
              setScreen(nextScreen)
              setRewardDuration(rewardDuration)
            }}
            onFailure={(nextScreen, errorMessage) => {
              setScreen(nextScreen)
              setErrorMessage(errorMessage)
            }}
          />
        )}
        {screen === Screen.PasswordCreate && (
          <PasswordCreateScreen
            assignedLocale={assignedLocale}
            email={email}
            promoCode={promoCode}
            gympassToken={gympassToken}
            fitnessOnDemandToken={fitnessOnDemandToken}
            promoLinkToken={promoLinkToken}
            onComplete={(nextScreen, rewardDuration) => {
              setScreen(nextScreen)
              setRewardDuration(rewardDuration)
            }}
            onFailure={(nextScreen, errorMessage) => {
              setScreen(nextScreen)
              setErrorMessage(errorMessage)
            }}
          />
        )}
        {screen === Screen.SuccessExisting && (
          <SuccessScreen
            existing
            rewardDuration={rewardDuration}
            gympassToken={gympassToken}
            fitnessOnDemandToken={fitnessOnDemandToken}
            promoLinkToken={promoLinkToken}
          />
        )}
        {screen === Screen.SuccessCreated && (
          <SuccessScreen
            created
            rewardDuration={rewardDuration}
            gympassToken={gympassToken}
            fitnessOnDemandToken={fitnessOnDemandToken}
            promoLinkToken={promoLinkToken}
          />
        )}
        {screen === Screen.Failed && (
          <FailedScreen
            message={errorMessage}
            gympassToken={gympassToken}
            fitnessOnDemandToken={fitnessOnDemandToken}
            promoLinkToken={promoLinkToken}
            onTryAgain={onTryAgain}
          />
        )}
      </header>
    </div>
  )
}
